import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import {
  BookOpen, LayoutDashboard, Users, PlusCircle, KeyRound, Printer, ScanLine,
  Trophy, BarChart3, MessageSquareShare, FileText, Settings as SettingsIcon,
  Building2, HelpCircle, Lightbulb,
} from "lucide-react";

const Step = ({ children }) => (
  <li className="flex gap-2 text-sm leading-relaxed">
    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
    <span>{children}</span>
  </li>
);

const Tip = ({ children }) => (
  <div className="flex gap-2 rounded-lg border border-amber-300/50 bg-amber-50 dark:bg-amber-950/20 p-3 text-sm">
    <Lightbulb className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
    <span>{children}</span>
  </div>
);

const SECTIONS = [
  {
    id: "baslangic", icon: LayoutDashboard, title: "1. Başlarken — Genel Bakış",
    body: (
      <>
        <p className="text-sm text-muted-foreground mb-3">HAFTALIK KTT; öğrenci ve sınıf yönetiminden optik form üretimine, telefonla optik okumadan sonuç analizine ve veli bildirimlerine kadar tüm Konu Tarama Testi (KTT) sürecini tek yerde yönetmenizi sağlar.</p>
        <ul className="space-y-2">
          <Step><b>Genel Bakış</b> ekranı; kayıtlı sınav, öğrenci, sınıf sayıları ve son KTT özetini gösterir.</Step>
          <Step>Sol menü üç gruba ayrılır: <b>Ana Modüller</b> (sınav/öğrenci yönetimi), <b>Sınav & Optik</b> (okuma ve analiz), <b>İletişim & Sistem</b> (veli, rapor, ayar, kılavuz).</Step>
          <Step>Sağ üstten <b>tema rengini</b> ve <b>açık/koyu</b> modu değiştirebilirsiniz.</Step>
        </ul>
      </>
    ),
  },
  {
    id: "ogrenci", icon: Users, title: "2. Öğrenciler ve Sınıflar",
    body: (
      <ul className="space-y-2">
        <Step><b>Sınıflar & Şubeler</b> ekranından sınıf/şube (örn. 5-A) oluşturun.</Step>
        <Step><b>Öğrenciler</b> ekranından öğrenci ekleyin: ad, soyad, okul numarası, sınıf ve şube.</Step>
        <Step>Çok sayıda öğrenciyi <b>Excel ile toplu</b> yükleyebilirsiniz. Önce örnek şablonu indirin, doldurun, sonra yükleyin.</Step>
        <Step>Öğrenci numarası optik formdaki QR kod ile eşleşir; bu yüzden numaraların doğru olması önemlidir.</Step>
      </ul>
    ),
  },
  {
    id: "sinav", icon: PlusCircle, title: "3. Sınav (KTT) Oluşturma",
    body: (
      <>
        <ul className="space-y-2">
          <Step><b>Sınav Oluştur</b> ekranından sınav adı, KTT numarası, tarih ve süreyi girin.</Step>
          <Step>Hangi derslerin sınava gireceğini seçin. <b>Hazır şablonlar</b> ile hızlıca seçim yapabilirsiniz: Tüm Dersler, Sadece Matematik, Sayısal, Sözel vb.</Step>
          <Step>Her ders için <b>soru sayısını</b> girin.</Step>
        </ul>
        <div className="mt-3"><Tip><b>Soru sayısı girilmeyen (boş/0) ders optikte görünmez.</b> Yalnız soru sayısı yazdığınız dersler optik forma ve sonuçlara dahil edilir.</Tip></div>
      </>
    ),
  },
  {
    id: "cevap", icon: KeyRound, title: "4. Cevap Anahtarı",
    body: (
      <>
        <ul className="space-y-2">
          <Step><b>Sınavlar → Cevap Anahtarı</b> ekranından her sorunun doğru cevabını girin veya Excel ile yükleyin.</Step>
          <Step>Sınıf bazında farklı cevap anahtarı tanımlayabilirsiniz (örn. 5. sınıf Fen için ayrı anahtar).</Step>
          <Step><b>Cevap anahtarını değiştirdiğinizde, optiği tekrar okutmanıza gerek yoktur — netler otomatik olarak yeniden hesaplanır.</b></Step>
          <Step>İsterseniz <b>Sonuçlar</b> ekranındaki <b>"Netleri Güncelle"</b> butonuyla da sonuçları anında yeniden hesaplatabilirsiniz.</Step>
        </ul>
      </>
    ),
  },
  {
    id: "optik-form", icon: Printer, title: "5. Optik Form Oluşturma ve Yazdırma",
    body: (
      <>
        <ul className="space-y-2">
          <Step><b>Sınavlar → Optik Formlar</b> ekranından seçilen sınıf için optik formları üretin.</Step>
          <Step>Her öğrenci için ad-soyad, numara ve <b>QR kod</b> içeren kişiye özel optik form oluşturulur.</Step>
          <Step>Soru numaralandırması <b>her ders için 1'den başlar</b> (örn. Matematik 1-15, ardından Türkçe 1-10).</Step>
          <Step><b>Hasta/izinli öğrencileri hariç tutabilirsiniz;</b> seçtiğiniz öğrenciler için form üretilmez.</Step>
          <Step>Formları tek PDF olarak indirip yazıcıdan çıktı alın.</Step>
        </ul>
        <div className="mt-3"><Tip>En iyi okuma için formları <b>net, düz ve köşe işaretleri görünür</b> şekilde yazdırın; fotokopide soluklaşmamasına dikkat edin.</Tip></div>
      </>
    ),
  },
  {
    id: "optik-oku", icon: ScanLine, title: "6. Optik Okuma (QR + Telefon Kamerası)",
    body: (
      <>
        <ul className="space-y-2">
          <Step><b>Optik Okuma</b> ekranını telefon veya bilgisayar kamerasıyla kullanabilirsiniz.</Step>
          <Step>İki aşamalı akış: önce <b>QR kodu okutun</b> (öğrenci ve form otomatik bulunur), ardından <b>"Optiği Oku"</b> ile işaretli baloncuklar okunur.</Step>
          <Step>Formu düz, iyi ışıkta ve parlamasız şekilde çerçeveye tam sığdırın.</Step>
          <Step>Okunan cevaplar kaydedilir; net ve puan otomatik hesaplanır.</Step>
        </ul>
        <div className="mt-3"><Tip>QR görünmüyorsa: forma daha yakın tutun, ışığı artırın ve parlamayı önleyin. QR yalnız öğrenciyi bulmak içindir; baloncukları "Optiği Oku" adımı okur.</Tip></div>
      </>
    ),
  },
  {
    id: "sonuc", icon: Trophy, title: "7. Sonuçlar ve Okunan Optik",
    body: (
      <ul className="space-y-2">
        <Step><b>Sınavlar → Sonuçlar</b> ekranında her öğrencinin doğru/yanlış/boş, net ve puanını görürsünüz.</Step>
        <Step>Her satırdaki <b>göz ikonu ("Optik Gör")</b> ile öğrencinin <b>okunan optiğini tekrar görüntüleyebilirsiniz:</b> hangi soruya ne işaretlediği, doğru cevap ve durum (doğru/yanlış/boş) ders ders gösterilir. Optiği tekrar okutmaya gerek yoktur.</Step>
        <Step>Sonuçları <b>Excel/CSV</b> olarak dışa aktarabilir, toplu <b>PDF karne</b> üretebilirsiniz.</Step>
        <Step><b>Netleri Güncelle</b> butonu, cevap anahtarı değişikliğinden sonra tüm netleri yeniden hesaplar.</Step>
      </ul>
    ),
  },
  {
    id: "analiz", icon: BarChart3, title: "8. Analizler (Sınıf / Öğrenci / KTT)",
    body: (
      <ul className="space-y-2">
        <Step><b>Sınıf Analizi:</b> seçilen sınıfın ders ve konu bazlı başarısı, sınıf ortalamaları.</Step>
        <Step><b>Öğrenci Analizi:</b> bir öğrencinin KTT'ler boyunca gelişimi, güçlü ve zayıf dersleri.</Step>
        <Step><b>KTT Analizi:</b> tüm okulun/sınıfların KTT bazında karşılaştırmalı performansı ve grafikleri.</Step>
      </ul>
    ),
  },
  {
    id: "veli", icon: MessageSquareShare, title: "9. Veli Bildirimleri",
    body: (
      <>
        <ul className="space-y-2">
          <Step><b>Veli Bildirimleri</b> ekranından, öğrencinin karnesine benzeyen e-postayı velilere gönderirsiniz.</Step>
          <Step>E-posta; ders tablosu, net/puan, güçlü ve geliştirilmesi gereken dersler, yanlış sorular ve tekrar edilmesi gereken konuları içerir.</Step>
          <Step>Göndermeden önce <b>önizleme</b> yapabilirsiniz.</Step>
          <Step>Geçersiz/hatalı yazılmış veli e-posta adresleri otomatik atlanır ve size bildirilir.</Step>
        </ul>
        <div className="mt-3"><Tip>Çok sayıda veliye gönderimde sistem, e-posta sağlayıcısının hız sınırına takılmamak için araya kısa beklemeler koyar; bu yüzden toplu gönderim birkaç dakika sürebilir.</Tip></div>
      </>
    ),
  },
  {
    id: "rapor", icon: FileText, title: "10. Raporlar, Karneler ve Haftanın Öğrencileri",
    body: (
      <ul className="space-y-2">
        <Step><b>Raporlar & Karneler</b> ekranından öğrenci karnelerini ve sınav raporlarını PDF olarak üretin.</Step>
        <Step><b>Haftanın Öğrencileri</b>: seçtiğiniz KTT'nin şampiyonlarını gösteren yatay afiş (JPEG) oluşturabilirsiniz.</Step>
        <Step>Geçmiş haftaların şampiyonlarını ve net/puan değişimlerini görebilirsiniz.</Step>
      </ul>
    ),
  },
  {
    id: "ayar", icon: SettingsIcon, title: "11. Ayarlar",
    body: (
      <ul className="space-y-2">
        <Step><b>Kurum bilgileri:</b> okul/kurum adı, akademik yıl ve dönem.</Step>
        <Step><b>Logo:</b> kurum logonuzu yükleyin (PNG/JPEG/WEBP, en fazla 5 MB). Logo; optik form, karne ve rapor başlıklarında kullanılır.</Step>
        <Step><b>Tema rengi</b> ve görünüm tercihleri.</Step>
        <Step><b>Kullanıcılar:</b> yöneticiler, öğretmen hesapları ekleyebilir ve rollerini belirleyebilir.</Step>
      </ul>
    ),
  },
  {
    id: "kurum", icon: Building2, title: "12. Kurum Yönetimi (Süper Yönetici)",
    body: (
      <ul className="space-y-2">
        <Step>Yalnız <b>Süper Yönetici</b> için görünür. Tüm kurumları listeler.</Step>
        <Step>Kurum deneme süresi, kilitleme/aktifleştirme ve abonelik durumunu yönetir.</Step>
        <Step>Her kurumun verisi birbirinden <b>izole</b>dir; kurumlar birbirinin verisini göremez.</Step>
      </ul>
    ),
  },
  {
    id: "sss", icon: HelpCircle, title: "13. Sık Sorulan Sorular / Sorun Giderme",
    body: (
      <ul className="space-y-3">
        <li className="text-sm"><b>Optikte bir ders görünmüyor?</b> O derse soru sayısı girilmemiştir. Sınavı düzenleyip soru sayısını girin.</li>
        <li className="text-sm"><b>Netler yanlış çıktı?</b> Cevap anahtarını kontrol edin, düzeltin; netler otomatik güncellenir. Gerekirse "Netleri Güncelle" butonunu kullanın.</li>
        <li className="text-sm"><b>QR okunmuyor?</b> Forma yaklaşın, ışığı artırın, parlamayı önleyin. QR yalnız öğrenciyi bulur; baloncukları "Optiği Oku" adımı okur.</li>
        <li className="text-sm"><b>Öğrencinin ne işaretlediğini görmek istiyorum.</b> Sonuçlar ekranında ilgili öğrencinin göz ikonuna ("Optik Gör") basın.</li>
        <li className="text-sm"><b>Veli e-postası gitmedi?</b> Veli adresinin doğru yazıldığından emin olun; hatalı adresler atlanır ve listede belirtilir.</li>
      </ul>
    ),
  },
];

export default function Guide() {
  return (
    <div className="space-y-6" data-testid="guide-page">
      <PageHeader title="Kullanım Kılavuzu" desc="Sistemin tüm özellikleri ve adım adım kullanımı" />

      <Card className="p-5 bg-gradient-to-br from-primary/5 to-transparent border-primary/20">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-xl bg-primary/10 grid place-items-center shrink-0">
            <BookOpen className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold">Her şey burada</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Öğrenci kaydından optik okumaya, sonuç analizinden veli bildirimine kadar HAFTALIK KTT'nin tüm adımlarını aşağıdaki başlıklardan inceleyebilirsiniz. Bir başlığa dokunarak ayrıntıları açın.
            </p>
          </div>
        </div>
      </Card>

      <Card className="p-2 sm:p-4">
        <Accordion type="multiple" className="w-full" data-testid="guide-accordion">
          {SECTIONS.map((sec) => (
            <AccordionItem key={sec.id} value={sec.id} data-testid={`guide-section-${sec.id}`}>
              <AccordionTrigger className="hover:no-underline">
                <span className="flex items-center gap-3 text-left">
                  <sec.icon className="h-[18px] w-[18px] text-primary shrink-0" />
                  <span className="font-semibold text-sm sm:text-base">{sec.title}</span>
                </span>
              </AccordionTrigger>
              <AccordionContent className="pl-1 sm:pl-9 pr-1 pb-4">{sec.body}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Card>
    </div>
  );
}
